import { useProductNudge } from './useProductNudge';

import { generateSellerKey } from '@/modules/bag/helpers';
import {
  type BagDetailedSellerCache,
  isOptimisticProduct,
} from '@/modules/bag/types';
import { type ProductNudges } from '@/modules/nudges/aliases';
import { isSold } from '@/modules/product/helpers/isSold';

/**
 * Custom hook that collects all product IDs from bag sellers
 * and fetches nudges for them in a single batch request
 */
export function useBagProductNudges(sellers?: BagDetailedSellerCache[]) {
  // Collect all product IDs from all sellers
  const allProductIds = sellers
    ?.flatMap((sellerData) =>
      sellerData.products
        .filter(
          (product) => !isOptimisticProduct(product) && !isSold(product.status)
        )
        .map((product) => product.id)
    )
    .filter(Boolean) as number[] | undefined;

  // Fetch all nudges in a single request
  const { data: nudgesData } = useProductNudge({
    productIds: allProductIds || [],
    enabled: !!allProductIds?.length,
  });

  // Create a mapping of nudges by seller
  const nudgesBySeller =
    !sellers || !nudgesData
      ? {}
      : sellers.reduce<Record<string, ProductNudges>>((acc, sellerData) => {
          const sellerKey = generateSellerKey(sellerData);

          // Extract product IDs for this specific seller
          const sellerProductIds = sellerData.products
            .filter(
              (product) =>
                !isOptimisticProduct(product) && !isSold(product.status || '')
            )
            .map((product) => product.id) as number[];

          // Create a filtered nudges object containing only this seller's products
          acc[sellerKey] = sellerProductIds.reduce((nudgesAcc, productId) => {
            if (nudgesData[productId]) {
              nudgesAcc[productId] = nudgesData[productId];
            }
            return nudgesAcc;
          }, {} as ProductNudges);
          return acc;
        }, {});

  return {
    nudgesData,
    nudgesBySeller,
  };
}
